import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useChartOfAccounts,
  type ChartAccountTree,
  type CreateAccountInput,
  ACCOUNT_TYPE_LABELS,
} from "@/hooks/useChartOfAccounts";
import { AccountFormDialog } from "@/components/chart-of-accounts/AccountFormDialog";
import { AccountTree } from "@/components/chart-of-accounts/AccountTree";
import { Plus, RefreshCw, Search } from "lucide-react";
import { useMemo } from "react";

export default function PlanoContasPage() {
  const {
    accounts,
    flatTree,
    isLoading,
    initializeAccounts,
    createAccount,
    updateAccount,
    toggleAccountActive,
  } = useChartOfAccounts();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [editingAccount, setEditingAccount] = useState<ChartAccountTree | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [showInactive, setShowInactive] = useState(false);

  const filteredAccounts = useMemo(() => {
    return flatTree.filter((account) => {
      // Status filter
      if (!showInactive && !account.is_active) return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (
          !account.code.toLowerCase().includes(query) &&
          !account.name.toLowerCase().includes(query)
        ) {
          return false;
        }
      }

      // Type filter
      if (typeFilter !== "all" && account.account_type !== typeFilter) {
        return false;
      }

      return true;
    });
  }, [flatTree, searchQuery, typeFilter, showInactive]);

  const handleOpenCreate = () => {
    setEditingAccount(null);
    setDialogMode("create");
    setDialogOpen(true);
  };

  const handleOpenEdit = (account: ChartAccountTree) => {
    setEditingAccount(account);
    setDialogMode("edit");
    setDialogOpen(true);
  };

  const handleSubmit = async (values: CreateAccountInput): Promise<boolean> => {
    if (dialogMode === "edit" && editingAccount) {
      return await updateAccount(editingAccount.id, values);
    } else {
      const id = await createAccount(values);
      return !!id;
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    await toggleAccountActive(id, isActive);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setTypeFilter("all");
    setShowInactive(false);
  };

  const hasActiveFilters = searchQuery || typeFilter !== "all" || showInactive;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Plano de Contas</h1>
          <p className="text-muted-foreground">
            Gerencie as contas para classificação financeira e DRE
          </p>
        </div>

        <div className="flex gap-2">
          {accounts.length === 0 && !isLoading && (
            <Button onClick={initializeAccounts} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />
              Inicializar Plano de Contas
            </Button>
          )}
          <Button onClick={handleOpenCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Conta
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por código ou nome..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Tipo de conta" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            {Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant={showInactive ? "secondary" : "outline"}
          onClick={() => setShowInactive(!showInactive)}
        >
          {showInactive ? "Ocultando inativas" : "Mostrar inativas"}
        </Button>

        {hasActiveFilters && (
          <Button variant="ghost" onClick={clearFilters}>
            Limpar filtros
          </Button>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <AccountTree
          accounts={filteredAccounts}
          onEdit={handleOpenEdit}
          onToggleActive={handleToggleActive}
        />
      )}

      <AccountFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={dialogMode}
        account={editingAccount}
        parentAccounts={accounts}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
